import React, { useState, useEffect, memo, useContext } from 'react';
import { CmsContentSlotLoadingContext } from '../CmsDynamicContentSlot/CmsDynamicContentSlot';
import { css, useTheme } from '@emotion/react';
import { useRecoilValue } from 'recoil';
import { selectUserDetails, selectedStateAtom, selectUserIsUnlimited } from 'common-state';
import { type ConditionalContainerEntryType } from 'tsconfig/types.d';
import { useScreenLayout } from '../../../hooks/useScreenLayout';
import USStateData from '../../../utils/USStateData';
import { isBrowser } from 'common-state/src/helpers/utils';
import BannerSkeleton from '../../Skeleton/Banner/Banner';

export const BREAKPOINTS = {
  MOBILE: 'Mobile only (small device)',
  DESKTOP: 'Desktop only (large device)',
} as const;

export type CmsConditionalContainerProps = {
  children: React.ReactNode;
  contentItem: ConditionalContainerEntryType;
  enableLoader?: boolean;
};

const CHOOSE_A_VALUE = 'Choose a value';

/** Check if a Contentful dropdown field is unset (empty, undefined, null, or "Choose a value") */
export const isUnset = (value: string | undefined | null): boolean => !value || value === CHOOSE_A_VALUE;

/** Check if only breakpoint condition is set (all API-driven fields unset) */
const isBreakpointOnly = (fields: ConditionalContainerEntryType['fields']): boolean => {
  if (!fields || isUnset(fields.conditionBreakpoint)) {
    return false;
  }
  return (
    isUnset(fields.customerLoggedIn) &&
    isUnset(fields.conditionSubscription) &&
    isUnset(fields.conditionVineyardPartner) &&
    isUnset(fields.conditionWineFund) &&
    isUnset(fields.conditionUnlimited) &&
    isUnset(fields.conditionWineryDirect) &&
    isUnset(fields.conditionState)
  );
};

/** Check if no conditions are set at all */
const hasNoConditions = (fields: ConditionalContainerEntryType['fields']): boolean => {
  if (!fields) {
    return true;
  }
  return (
    isUnset(fields.customerLoggedIn) &&
    isUnset(fields.conditionSubscription) &&
    isUnset(fields.conditionVineyardPartner) &&
    isUnset(fields.conditionWineFund) &&
    isUnset(fields.conditionUnlimited) &&
    isUnset(fields.conditionBreakpoint) &&
    isUnset(fields.conditionWineryDirect) &&
    isUnset(fields.conditionState)
  );
};

/** Check if any user-API-dependent conditions are set (login, subscription, VP, winefund, unlimited) */
export const hasUserDependentConditions = (fields: ConditionalContainerEntryType['fields']): boolean => {
  if (!fields) {
    return false;
  }
  return (
    !isUnset(fields.customerLoggedIn) ||
    !isUnset(fields.conditionSubscription) ||
    !isUnset(fields.conditionVineyardPartner) ||
    !isUnset(fields.conditionWineFund) ||
    !isUnset(fields.conditionUnlimited)
  );
};

/**
 * Inner component for API-driven conditions (login, subscription, state, etc.)
 * Extracted so Recoil / useScreenLayout hooks are only called when needed.
 */
const CmsConditionalContainerWithJsConditions = memo(
  ({ children, contentItem, enableLoader }: CmsConditionalContainerProps) => {
    const userDetails = useRecoilValue(selectUserDetails);
    const selectedState = useRecoilValue(selectedStateAtom);
    const isUnlimitedUser = useRecoilValue(selectUserIsUnlimited);
    const theme = useTheme();
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const loadingContext = useContext(CmsContentSlotLoadingContext);
    const { screen } = useScreenLayout();

    const {
      customerLoggedIn,
      conditionSubscription,
      conditionVineyardPartner,
      conditionWineFund,
      conditionUnlimited,
      conditionBreakpoint,
      conditionWineryDirect,
      conditionState,
    } = contentItem?.fields || {};

    const wrapper = css`
      height: 100%;
    `;

    const hiddenWrapper = css`
      visibility: hidden;
      height: 0;
      overflow: hidden;
      width: 0;
    `;

    useEffect(() => {
      // If user-dependent conditions are set but user data hasn't loaded yet,
      // keep both isLoading=true and isVisible=false to prevent flash of wrong content.
      const needsUserData = hasUserDependentConditions(contentItem?.fields);
      if (needsUserData && !userDetails?.userLoded && loadingContext) {
        return;
      }

      //conditionUnlimited
      const userUnlimited = () => {
        if (isBrowser()) {
          return !(
            (conditionUnlimited === 'Yes' && !isUnlimitedUser) ||
            (conditionUnlimited === 'No' && isUnlimitedUser)
          );
        }
      };

      //conditionSubscription - Wineplan
      const subscribedUser = () => {
        if (isBrowser()) {
          const userWPStatus = userDetails?.subscriptions?.some(
            subscription => subscription?.type === 'WINEPLAN' && subscription?.status === 'ACTIVE',
          );
          return !(
            (conditionSubscription === 'Yes' && !userWPStatus) ||
            (conditionSubscription === 'No' && userWPStatus)
          );
        }
      };

      //conditionVineyardPartner
      const vineyardPartner = () => {
        if (isBrowser()) {
          if (conditionVineyardPartner === 'Yes') {
            return userDetails?.subscriptions?.some(subscribedUserDetails => subscribedUserDetails?.type === 'VP');
          }
          return true;
        }
      };

      //conditionWineFund
      const wineFund = () => {
        if (isBrowser()) {
          if (conditionWineFund === 'Yes') {
            return userDetails?.subscriptions?.some(
              subscribedUserDetails => subscribedUserDetails?.type === 'WINEFUND',
            );
          }
          return true;
        }
      };

      //User LoggedIn Check
      const userLoggedInCheck = () => {
        if (isBrowser()) {
          if (customerLoggedIn && customerLoggedIn !== CHOOSE_A_VALUE) {
            if (userDetails) {
              const loginType = userDetails.loginType === 'hard_logged' || userDetails.loginType === 'soft_logged';
              if (customerLoggedIn === 'Logged in' && loginType) {
                return true;
              } else if (customerLoggedIn === 'Not logged in' && !loginType) {
                return true;
              }
              return false;
            }
            return false;
          }
          return true;
        }
      };
      //Screen Breakpoint Check
      const screenBreakpointCheck = () => {
        if (globalThis.window === undefined) {
          return false;
        } // Ensure function returns false if window is undefined (SSR)

        if (!conditionBreakpoint || conditionBreakpoint === CHOOSE_A_VALUE) {
          return true;
        }

        const isMobileBreakpoint = globalThis.window.matchMedia(`(max-width: ${theme.breakpointWidths?.lg}px)`).matches;
        switch (conditionBreakpoint) {
          case BREAKPOINTS.MOBILE:
            return isMobileBreakpoint;
          case BREAKPOINTS.DESKTOP:
            return !isMobileBreakpoint;
          default:
            return false;
        }
      };
      //US State Check
      const stateDataCheck = () => {
        let stateCheckValid = true;
        if (conditionState || conditionWineryDirect) {
          if (selectedState) {
            const selectedStateData = USStateData?.find(stateData => stateData?.stateCode === selectedState);
            if (selectedStateData) {
              if (conditionState && conditionState !== selectedStateData.stateName) {
                stateCheckValid = false;
              }
              if (conditionWineryDirect) {
                stateCheckValid =
                  (conditionWineryDirect === 'Yes' && selectedStateData.wineryDirect) ||
                  (conditionWineryDirect === 'No' && !selectedStateData.wineryDirect);
              }
            } else {
              stateCheckValid = false;
            }
          } else {
            stateCheckValid = false;
          }
        }
        return stateCheckValid;
      };

      const checks = [
        userLoggedInCheck(),
        screenBreakpointCheck(),
        stateDataCheck(),
        subscribedUser(),
        vineyardPartner(),
        wineFund(),
        userUnlimited(),
      ];

      const shouldRender = checks.every(Boolean);
      setIsLoading(!shouldRender);
      setIsVisible(shouldRender);
      // If we have a parent loading context, stop loading when client updates are done
      if (loadingContext && shouldRender && isLoading) {
        const [, setParentLoading] = loadingContext;
        setParentLoading(false);
      }
    }, [
      screen,
      conditionBreakpoint,
      selectedState,
      conditionState,
      conditionWineryDirect,
      customerLoggedIn,
      conditionSubscription,
      conditionVineyardPartner,
      conditionWineFund,
      conditionUnlimited,
      isUnlimitedUser,
      userDetails,
    ]);

    if (isLoading && enableLoader) {
      return (
        <div
          data-test="widget-CmsConditionalContainer"
          className="cms-conditional-container cms-conditional-loading"
          css={hiddenWrapper}
        >
          {children}
        </div>
      );
    } else if (isVisible) {
      return (
        <div data-test="widget-CmsConditionalContainer" className="cms-conditional-container" css={wrapper}>
          {children}
        </div>
      );
    }

    return null;
  },
);

/**
 * CmsConditionalContainer — shows/hides CMS content based on Contentful conditions.
 *
 * Three rendering paths for optimal LCP:
 * 1. No conditions set → render immediately (SSG-safe)
 * 2. Breakpoint-only → CSS media query (SSG-safe, no JS needed)
 * 3. API-driven conditions → JS evaluation (client-only)
 */
export const CmsConditionalContainer = memo((props: CmsConditionalContainerProps) => {
  const { children, contentItem, enableLoader } = props;
  const theme = useTheme();
  const loadingContext = useContext(CmsContentSlotLoadingContext);
  const fields = contentItem?.fields;

  const noConditions = hasNoConditions(fields);
  const breakpointOnlyCondition = !noConditions && isBreakpointOnly(fields);
  const isFastPath = noConditions || breakpointOnlyCondition;

  // For fast paths, notify parent loading context on mount
  useEffect(() => {
    if (isFastPath && loadingContext) {
      const [, setParentLoading] = loadingContext;
      setParentLoading(false);
    }
  }, [isFastPath, loadingContext]);

  const wrapper = css`
    height: 100%;
  `;

  // ── Fast path 1: No conditions → render immediately (SSG-safe) ──
  if (noConditions) {
    return (
      <div data-test="widget-CmsConditionalContainer" className="cms-conditional-container" css={wrapper}>
        {children}
      </div>
    );
  }

  // ── Fast path 2: Breakpoint-only → CSS media query (SSG-safe) ──
  if (breakpointOnlyCondition) {
    const breakpointStyle =
      fields.conditionBreakpoint === BREAKPOINTS.MOBILE
        ? theme.widgets?.CmsConditionalContainer?.mobileOnly
        : theme.widgets?.CmsConditionalContainer?.desktopOnly;

    return (
      <div
        data-test="widget-CmsConditionalContainer"
        data-breakpoint={fields.conditionBreakpoint === BREAKPOINTS.MOBILE ? 'mobile' : 'desktop'}
        className="cms-conditional-container cms-conditional-breakpoint"
        css={css`
          height: 100%;
          ${breakpointStyle}
        `}
      >
        {children}
      </div>
    );
  }

  // Retrain the loader for Legacy content types that rely on client-side evaluation, to prevent flash of content
  // This is a temporary measure until all content is migrated to new conditional container content type that supports server-side evaluation of breakpoint conditions
  // It can be removed once the business started using dynamic content slot loading context for all conditional container content types, and all legacy content types are migrated or retired
  if (
    !loadingContext &&
    !isBrowser() &&
    fields?.content?.[0]?.sys.contentType.sys.id &&
    ['banner', 'carousel', 'image'].includes(fields?.content?.[0]?.sys.contentType.sys.id)
  ) {
    return <BannerSkeleton variant={'Content Right / Image Left'} />;
  }

  // ── Slow path: API-driven conditions → JS evaluation (client-only) ──
  return (
    <CmsConditionalContainerWithJsConditions contentItem={contentItem} enableLoader={enableLoader}>
      {children}
    </CmsConditionalContainerWithJsConditions>
  );
});
